import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { BuildingIcon, UsersIcon, TrendingUpIcon, NetworkIcon, PlayIcon, PauseIcon, Volume2Icon, VolumeXIcon } from 'lucide-react';
const stats = [{
  icon: BuildingIcon,
  label: 'Branches',
  value: '287+'
}, {
  icon: NetworkIcon,
  label: 'ATM Network',
  value: '950+'
}, {
  icon: UsersIcon,
  label: 'Customers',
  value: '4.8M+'
}, {
  icon: TrendingUpIcon,
  label: 'Asset Growth',
  value: '+8.6%'
}];
export function VideoCard() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const send = (func: string, args: any[] = []) => {
    iframeRef.current?.contentWindow?.postMessage(JSON.stringify({
      event: 'command',
      func,
      args
    }), '*');
  };
  const togglePlay = () => {
    if (playing) {
      send('pauseVideo');
    } else {
      send('playVideo');
    }
    setPlaying(p => !p);
  };
  const toggleMute = () => {
    if (muted) {
      send('unMute');
      send('setVolume', [80]);
    } else {
      send('mute');
    }
    setMuted(m => !m);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.7,
    ease: [0.22, 1, 0.36, 1]
  }} className="relative w-full h-full overflow-hidden shadow-[0_10px_30px_-12px_rgba(0,0,0,0.7)]" style={{
    border: '1px solid rgba(255,255,255,0.15)',
    background: '#000',
    borderRadius: 0
  }} data-id="element-133">
      {/* Video — scaled to cover the frame fully */}
      <div className="absolute inset-0 overflow-hidden" data-id="element-134">
        <iframe ref={iframeRef} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{
        width: '177.78vh',
        height: '56.25vw',
        minWidth: '100%',
        minHeight: '100%',
        pointerEvents: 'none'
      }} src="https://www.youtube.com/embed/tZYk8EUzbMo?autoplay=1&mute=1&loop=1&playlist=tZYk8EUzbMo&controls=0&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&playsinline=1&enablejsapi=1" title="Commercial Bank" frameBorder={0} allow="autoplay; encrypted-media" data-id="element-135" />
      </div>

      {/* Top-right control buttons */}
      <div className="absolute top-3 right-3 flex items-center gap-2" data-id="element-136">
        <button onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'} className="flex items-center justify-center h-10 w-10 backdrop-blur-xl transition-colors hover:bg-white/20" style={{
        background: 'rgba(3, 32, 63, 0.6)',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: 0
      }} data-id="element-137">
          {playing ? <PauseIcon className="h-4 w-4 text-white" data-id="element-138" /> : <PlayIcon className="h-4 w-4 text-white" data-id="element-139" />}
        </button>
        <button onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'} className="flex items-center justify-center h-10 w-10 backdrop-blur-xl transition-colors hover:bg-white/20" style={{
        background: 'rgba(3, 32, 63, 0.6)',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: 0
      }} data-id="element-140">
          {muted ? <VolumeXIcon className="h-4 w-4 text-white" data-id="element-141" /> : <Volume2Icon className="h-4 w-4 text-[#9ed957]" data-id="element-142" />}
        </button>
      </div>

      {/* Strong bottom-edge gradient for legibility behind the stats overlay */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/95 via-black/60 to-transparent" data-id="element-143" />

      {/* High-contrast stats bar — readable from a distance */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.4,
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }} className="absolute left-3 right-3 bottom-3 px-5 py-3" style={{
      background: 'rgba(2, 22, 44, 0.92)',
      border: '1px solid rgba(127, 181, 57, 0.45)',
      boxShadow: '0 10px 30px -12px rgba(0,0,0,0.8)',
      borderRadius: 0
    }} data-id="element-144">
        <div className="flex items-center justify-between mb-2.5" data-id="element-145">
          <div className="flex items-center gap-2" data-id="element-146">
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#7FB539] pulse-dot" data-id="element-147" />
            <span className="text-[12px] uppercase text-white font-extrabold tracking-wider" data-id="element-148">
              Bank at a Glance
            </span>
          </div>
          <span className="text-[12px] uppercase text-white/85 font-bold tracking-wider" data-id="element-149">
            Commercial Bank · FY 25/26
          </span>
        </div>

        <div className="grid grid-cols-4 gap-4" data-id="element-150">
          {stats.map((s, i) => {
          const Icon = s.icon;
          return <motion.div key={s.label} initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6 + i * 0.07,
            duration: 0.5
          }} className="flex items-center gap-3" data-id="element-151">
                <div className="flex items-center justify-center h-11 w-11 shrink-0" style={{
              background: '#0066B3',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 0
            }} data-id="element-152">
                  <Icon className="h-6 w-6 text-white" data-id="element-153" />
                </div>
                <div className="min-w-0 leading-tight" data-id="element-154">
                  <div className="text-[11px] uppercase text-white/85 font-bold" data-id="element-155">
                    {s.label}
                  </div>
                  <div className="font-mono-num text-2xl font-extrabold text-white" data-id="element-156">
                    {s.value}
                  </div>
                </div>
              </motion.div>;
        })}
        </div>
      </motion.div>
    </motion.div>;
}